import React from 'react';
import AttributeRow from './AttributeRow';
import EmptyState from './EmptyState';

export default function AttributeTable({
  expanded,
  toggle,
  openModal,
  openTermModal,
  openEditModal,
  openAttributeEditModal,
  refreshCounter,
}) {
  const [attributes, setAttributes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const fetchAttributes = React.useCallback(async (opts = { setLoading: true }) => {
    let mounted = true;
    if (opts.setLoading) setLoading(true);
    try {
      const form = new FormData();
      form.append('action', 'tpwvs_get_attributes');
      const res = await fetch(window.tpwvs_admin_settings.api_url, { method: 'POST', body: form });
      const json = await res.json();
      if (!mounted) return;
      if (json.success) {
        setAttributes(json.data.data || []);
        setError(null);
      } else {
        setError(json.data && json.data.message ? json.data.message : 'Failed to load attributes');
      }
    } catch (err) {
      if (!mounted) return;
      setError(err.message || 'Fetch error');
    } finally {
      if (mounted) setLoading(false);
    }

    return () => { mounted = false; };
  }, []);

  React.useEffect(() => {
    fetchAttributes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    // refreshCounter changed -> re-fetch
    if (typeof refreshCounter !== 'undefined') {
      fetchAttributes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCounter]);

  if (loading) return <div className="p-6">Loading attributes…</div>;
  if (error) return (
    <div className="p-6">
      <div role="alert" className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 text-red-800 rounded-md">
        <svg aria-hidden="true" className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div className="text-sm leading-6">
          <div className="font-medium">Error</div>
          <div className="mt-0.5">{error}</div>
        </div>
      </div>
    </div>
  );

  if (!attributes || attributes.length === 0) {
    return <EmptyState openModal={openModal} />;
  }

  return (
    <div className="overflow-x-auto bg-white rounded-lg border border-gray2 shadow-sm">
      <table className="w-full text-sm text-left rtl:text-right text-body">
        <thead className="bg-gray-50 border-b border-gray2">
          <tr>
            <th className="px-4 py-2 text-left text-base font-semibold text-gray-700 uppercase tracking-wider">Attribute</th>
            <th className="px-4 py-2 text-left text-base font-semibold text-gray-700 uppercase tracking-wider">Taxonomy</th>
            <th className="px-4 py-2 text-center text-base font-semibold text-gray-700 uppercase tracking-wider">Terms</th>
            <th className="px-4 py-2 text-left text-base font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray2">
          {attributes.map((attr) => (
            <AttributeRow
              key={attr.taxonomy}
              attr={attr}
              expanded={!!expanded[attr.taxonomy]}
              toggle={toggle}
              openTermModal={openTermModal}
              openEditModal={openEditModal}
              openAttributeEditModal={openAttributeEditModal}
            />
          ))}
        </tbody>
      </table>
      <div className="p-4 border-t border-gray2 bg-white flex justify-end">
        <button onClick={openModal} className="text-[11px] inline-flex items-center px-2 py-1 bg-theme text-white rounded">Add attribute</button>
      </div>
    </div>
  );
}
